import { useState } from "react";
import { Button, Card, Loader, EmptyState, Pagination, PageHeader } from "../../../components/ui";
import { useAppMutation, useAppQuery } from "../../../api/fetchClient";
import { QUERY_KEYS } from "../../../api/queryKeys";
import { DELETE_HCP, GET_HCP_ASSIGN } from "../../../api/url";
import AddHcpsModal from "./AddHcpsModal";
import { plusIcon, eyeIcon as EyeIcon, editIcon, deleteIcon } from "../../../assets/Icons/icons";
import { strings } from "../../../strings/strings";
import ViewHcpModal from "./ViewHcpModal";
import DeletModel from "../../../components/model/deleteModel";
import { toast } from "react-toastify";
import { getErrorMessage } from "../StaticStrings/utils";

const Hcps = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [mode, setMode] = useState("add");
  const [selectedHcp, setSelectedHcp] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [viewHcp, setViewHcp] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [deleteId, setDeleteId] = useState(null);
  const { mutate, isPending: isMutating } = useAppMutation();

  const {
    data: hcpsData,
    isLoading: isHcpsLoading,
    isError: isHcpsError,
    error: hcpsError,
    refetch: refetchHcps,
  } = useAppQuery({
    queryKey: [QUERY_KEYS.HCP, currentPage],
    url: `${GET_HCP_ASSIGN}?page=${currentPage}`,
    method: "GET",
  });

  const hcpsList = Array.isArray(hcpsData?.results?.data)
    ? hcpsData.results.data
    : Array.isArray(hcpsData?.results)
      ? hcpsData.results
      : Array.isArray(hcpsData?.data)
        ? hcpsData.data
        : Array.isArray(hcpsData)
          ? hcpsData
          : [];

  const pagination = hcpsData?.results?.pagination || null;

  const handleOpenAdd = () => {
    setMode("add");
    setSelectedHcp(null);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (hcp) => {
    setMode("edit");
    setSelectedHcp(hcp);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setMode("add");
    setSelectedHcp(null);
  };

  const handleSuccess = () => {
    handleCloseModal();
    refetchHcps();
  };

  const handlePageChange = (newPage) => setCurrentPage(newPage);
  const handleOpenView = (hcp) => {
    setViewHcp(hcp);
    setIsViewOpen(true);
  };

  const handleCloseView = () => {
    setIsViewOpen(false);
    setViewHcp(null);
  };
const handleDelete = (hcp) => {
  setSelectedHcp(hcp);
  setShowDeleteModal(true);
};
  const handleCancelDelete = () => {
    setDeleteId(null);
    setShowDeleteModal(false);
  };

const handleConfirmDelete = () => {
  if (!selectedHcp) return;

  mutate(
    { url: `${DELETE_HCP}?id=${selectedHcp.id}`, method: "DELETE" },
    {
      onSuccess: (res) => {
        toast.success(res.message);
        setShowDeleteModal(false);
        setSelectedHcp(null);
        refetchHcps();
      },
      onError: (err) => toast.error(getErrorMessage(err)),
    },
  );
};

  return (
    <div>
      <div className="max-w-7xl mx-auto">
        <PageHeader title={strings.hcps} description={strings.hcpdescription} className="mb-6">
          <Button
            onClick={handleOpenAdd}
            variant="primary"
            size="md"
            className="shadow-md hover:shadow-lg transition-shadow"
          >
            <img src={plusIcon} alt="Add" className="h-5 w-5 inline-block mr-2" />
            {strings.addnewhcp}
          </Button>
        </PageHeader>
        <Card className="shadow-lg border-0">
          {isHcpsLoading ? (
            <div className="flex justify-center items-center py-20">
              <Loader />
            </div>
          ) : isHcpsError ? (
            <div className="py-12">
              <EmptyState
                description={hcpsError?.message || hcpsError?.response?.data?.message || strings.errorLoadingHcps}
              />
            </div>
          ) : hcpsList.length === 0 ? (
            <div className="py-12">
              <EmptyState title={strings.emptyHcp} description={strings.emptyHcpDesctiption} />
            </div>
          ) : (
            <div className="space-y-0">
              <div className="overflow-x-auto -mx-6 sm:mx-0">
                <div className="inline-block min-w-full align-middle">
                  <div className="overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200 rounded-lg">
                      <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                        <tr className="rounded-lg">
                          <th className="px-4 py-4 text-left text-xs font-bold text-secondary uppercase tracking-wider whitespace-nowrap">
                            {strings.refferalCode}
                          </th>
                          <th className="px-4 py-4 text-left text-xs font-bold text-secondary uppercase tracking-wider whitespace-nowrap">
                            {strings.firstName}
                          </th>
                          <th className="px-4 py-4 text-left text-xs font-bold text-secondary uppercase tracking-wider whitespace-nowrap">
                            {strings.lastName}
                          </th>
                          <th className="px-4 py-4 text-left text-xs font-bold text-secondary uppercase tracking-wider whitespace-nowrap">
                            {strings.mobileNumber2}
                          </th>
                          <th className="px-4 py-4 text-left text-xs font-bold text-secondary uppercase tracking-wider whitespace-nowrap">
                            {strings.email}
                          </th>
                          <th className="px-4 py-4 text-left text-xs font-bold text-secondary uppercase tracking-wider whitespace-nowrap">
                            {strings.hospitalName}
                          </th>
                          <th className="px-4 py-4 text-right text-xs font-bold text-secondary uppercase tracking-wider whitespace-nowrap">
                            {strings.action}
                          </th>
                        </tr>
                      </thead>

                      <tbody className="bg-white divide-y divide-gray-200">
                        {hcpsList.map((hcp, index) => (
                          <tr key={hcp.id || index} className="hover:bg-primary/5 transition-colors duration-150">
                            <td className="px-4 py-4 whitespace-nowrap">
                              <span className="inline-flex items-center px-3 py-1 rounded-md text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200 font-mono">
                                {hcp.referral_code || "-"}
                              </span>
                            </td>

                            <td className="px-4 py-4 whitespace-nowrap">
                              <span className="text-sm font-semibold text-secondary">{hcp.first_name || "-"}</span>
                            </td>

                            <td className="px-4 py-4 whitespace-nowrap">
                              <span className="text-sm font-semibold text-secondary">{hcp.last_name || "-"}</span>
                            </td>

                            <td className="px-4 py-4">
                              {hcp.mobile_code && hcp.mobile_number ? (
                                <span className="inline-flex items-center px-3 py-1 w-max text-sm text-secondary font-medium">
                                  {hcp.mobile_code} {hcp.mobile_number}
                                </span>
                              ) : (
                                <div className="flex justify-center items-center text-secondary">-</div>
                              )}
                            </td>

                            <td className="px-4 py-4 whitespace-nowrap">
                              <a href={`mailto:${hcp.email}`} className="text-sm font-medium text-secondary">
                                {hcp.email || "-"}
                              </a>
                            </td>

                            <td className="px-4 py-4">
                              <div className="text-sm text-secondary font-medium">{hcp.hospital_name || "-"}</div>
                            </td>

                            <td className="px-4 py-4 whitespace-nowrap text-right">
                              <div className="inline-flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => handleOpenView(hcp)}
                                  className="p-2 hover:bg-gray-100 bg-grey rounded-md transition-colors"
                                  title={strings.view || "View"}
                                >
                                  <EyeIcon className="w-3 h-3 text-secondary" />
                                </button>

                                <button
                                  type="button"
                                  onClick={() => handleOpenEdit(hcp)}
                                  className="p-2 hover:bg-gray-100 bg-grey rounded-md transition-colors"
                                  title={strings.edit || "Edit"}
                                >
                                  <img
                                    src={editIcon}
                                    alt={strings.edit}
                                    className="w-3 h-3 min-w-[10px] min-h-[10px]"
                                  />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDelete(hcp)}
                                  className="p-2 hover:bg-red-100 bg-grey rounded-md transition-colors"
                                  title={strings.delete || "Delete"}
                                >
                                  <img
                                    src={deleteIcon}
                                    alt={strings.delete}
                                    className="w-4 h-4 min-w-[10px] min-h-[10px]"
                                  />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {pagination && (
                <div className="py-5 border-t border-gray-200">
                  <Pagination
                    pagination={pagination}
                    onPageChange={handlePageChange}
                    currentItemsCount={hcpsList.length}
                  />
                </div>
              )}
            </div>
          )}
        </Card>

        <AddHcpsModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onSuccess={handleSuccess}
          mode={mode}
          hcp={selectedHcp}
        />
        {showDeleteModal && (
          <DeletModel 
            handleConfirmDelete={handleConfirmDelete} 
            handleCancelDelete={handleCancelDelete}
            isLoading={isMutating}
          />
        )}
        <ViewHcpModal isOpen={isViewOpen} onClose={handleCloseView} hcp={viewHcp} />
      </div>
    </div>
  );
};

export default Hcps;
